import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAppTranslations } from '@/hooks/useAppTranslations';

interface BubbleRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface MessageContextMenuProps {
  messageId: string;
  isOwnMessage: boolean;
  messageContent: string;
  onReply: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onCopy: () => void;
  onReaction?: (emoji: string) => void;
  onConvertToTask?: () => void;
  onDismiss?: () => void;
  bubbleRect?: BubbleRect;
  position?: { top?: string; left?: string; right?: string; bottom?: string };
  /** When provided, renders the bubble content inside the portal above the scrim */
  liftedBubble?: React.ReactNode;
}

const QUICK_REACTIONS = ['❤️', '👍', '😂', '😮', '😢', '🔥'];

const GLASS: React.CSSProperties = {
  background: 'rgba(242,242,247,0.82)',
  backdropFilter: 'blur(40px) saturate(200%) brightness(1.06)',
  WebkitBackdropFilter: 'blur(40px) saturate(200%) brightness(1.06)',
  border: '0.5px solid rgba(255,255,255,0.60)',
  boxShadow: '0 0 0 0.5px rgba(255,255,255,0.28) inset, 0 24px 64px rgba(0,0,0,0.24), 0 4px 16px rgba(0,0,0,0.10)',
};

const SEPARATOR: React.CSSProperties = {
  height: '0.5px',
  background: 'rgba(60,60,67,0.29)',
  margin: '0 16px',
};

export function MessageContextMenu({
  messageId,
  isOwnMessage,
  messageContent,
  onReply,
  onEdit,
  onDelete,
  onCopy,
  onReaction,
  onConvertToTask,
  onDismiss,
  bubbleRect,
  position,
  liftedBubble,
}: MessageContextMenuProps) {
  const { t } = useAppTranslations();
  const menuRef = useRef<HTMLDivElement>(null);

  // The same touch/click that opened the menu fires a touchend/click on the
  // overlay the instant the finger lifts. Swallow that first release so the
  // menu "holds" open (iMessage behaviour); arm dismissal a beat later.
  const armedRef = useRef(false);
  useEffect(() => {
    if (!bubbleRect) return;
    armedRef.current = false;
    const id = window.setTimeout(() => { armedRef.current = true; }, 350);
    return () => window.clearTimeout(id);
  }, [bubbleRect]);

  useEffect(() => {
    const el = menuRef.current;
    if (!el || bubbleRect) return;
    el.animate(
      [
        { opacity: 0, transform: 'scale(0.82)', transformOrigin: isOwnMessage ? 'bottom right' : 'bottom left' },
        { opacity: 1, transform: 'scale(1.03)', transformOrigin: isOwnMessage ? 'bottom right' : 'bottom left', offset: 0.65 },
        { opacity: 1, transform: 'scale(1.0)', transformOrigin: isOwnMessage ? 'bottom right' : 'bottom left' },
      ],
      { duration: 280, easing: 'cubic-bezier(0.34,1.56,0.64,1)', fill: 'forwards' }
    );
  }, [isOwnMessage, bubbleRect]);

  const align = isOwnMessage ? 'right' : 'left';

  // ── Portal mode — scrim only, bubble stays in DOM at elevated z-index ────
  if (bubbleRect) {
    const MENU_WIDTH = 240;
    const REACTION_HEIGHT = 56;
    const MENU_GAP = 8;
    const BUBBLE_GAP = 12;

    const screenW = window.innerWidth;
    const screenH = window.innerHeight;

    let menuLeft = isOwnMessage
      ? bubbleRect.x + bubbleRect.width - MENU_WIDTH
      : bubbleRect.x;
    menuLeft = Math.max(8, Math.min(menuLeft, screenW - MENU_WIDTH - 8));

    const spaceBelow = screenH - (bubbleRect.y + bubbleRect.height);
    const preferBelow = spaceBelow >= REACTION_HEIGHT + MENU_GAP + 200;

    let reactionTop: number;
    let menuTop: number;

    if (preferBelow) {
      reactionTop = bubbleRect.y + bubbleRect.height + BUBBLE_GAP;
      menuTop = reactionTop + REACTION_HEIGHT + MENU_GAP;
    } else {
      reactionTop = bubbleRect.y - BUBBLE_GAP - REACTION_HEIGHT;
      menuTop = Math.max(8, reactionTop - MENU_GAP - 220);
    }

    const dismiss = (e: React.MouseEvent | React.TouchEvent) => {
      e.stopPropagation();
      // Ignore the release of the opening gesture; only dismiss on a fresh tap.
      if (!armedRef.current) return;
      onDismiss?.();
    };

    const overlay = (
      <div
        style={{ position: 'fixed', inset: 0, zIndex: 999, pointerEvents: 'auto' }}
        onClick={dismiss}
        onTouchEnd={dismiss}
      >
        {/* Scrim */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0,0,0,0.45)',
          backdropFilter: 'blur(12px) brightness(0.7)',
          WebkitBackdropFilter: 'blur(12px) brightness(0.7)',
        }} />

        {/* Lifted bubble — rendered above scrim, below reaction pill */}
        {liftedBubble && bubbleRect && (
          <div
            style={{
              position: 'absolute',
              top: bubbleRect.y,
              left: bubbleRect.x,
              width: bubbleRect.width,
              zIndex: 1000,
              filter: 'drop-shadow(0 12px 32px rgba(0,0,0,0.35))',
              pointerEvents: 'none',
            }}
            onClick={e => e.stopPropagation()}
            onTouchEnd={e => e.stopPropagation()}
          >
            {liftedBubble}
          </div>
        )}

        {/* Reaction pill */}
        {onReaction && (
          <div
            className="context-menu"
            style={{ position: 'absolute', top: reactionTop, left: menuLeft, zIndex: 1001 }}
            onClick={e => e.stopPropagation()}
            onTouchEnd={e => e.stopPropagation()}
          >
            <PortalPill onReaction={onReaction} isOwnMessage={isOwnMessage} />
          </div>
        )}

        {/* Action menu */}
        <div
          className="context-menu"
          style={{ position: 'absolute', top: menuTop, left: menuLeft, width: MENU_WIDTH, zIndex: 1001 }}
          onClick={e => e.stopPropagation()}
          onTouchEnd={e => e.stopPropagation()}
        >
          <PortalMenu
            messageId={messageId}
            isOwnMessage={isOwnMessage}
            onReply={onReply}
            onEdit={onEdit}
            onDelete={onDelete}
            onCopy={onCopy}
            onConvertToTask={onConvertToTask}
            t={t}
          />
        </div>
      </div>
    );

    return createPortal(overlay, document.body);
  }

  // ── Inline fallback ──────────────────────────────────────────────────────
  return (
    <div style={{ position: 'absolute', zIndex: 50, [align]: 0, top: position?.top || '100%', marginTop: 8 }}>
      {onReaction && (
        <div style={{
          ...GLASS,
          borderRadius: 999,
          padding: '6px 10px',
          display: 'flex',
          gap: 2,
          marginBottom: 8,
          width: 'fit-content',
          marginLeft: align === 'right' ? 'auto' : undefined,
        }}>
          {QUICK_REACTIONS.map(emoji => (
            <button
              key={emoji}
              onClick={e => { e.stopPropagation(); onReaction(emoji); }}
              style={{
                minWidth: 44, minHeight: 44, width: 38, height: 38, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 22, background: 'transparent',
                transition: 'transform 120ms cubic-bezier(0.34,1.56,0.64,1)',
              }}
              onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.25)')}
              onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
            >{emoji}</button>
          ))}
        </div>
      )}
      <div ref={menuRef} data-message-id={messageId} style={{ ...GLASS, borderRadius: 20, minWidth: 220, overflow: 'hidden' }}>
        <Row label={t.messaging.reply} onPress={e => { e.stopPropagation(); onReply(); }}><IconReply /></Row>
        {isOwnMessage && <><Divider /><Row label={t.messaging.edit} onPress={e => { e.stopPropagation(); onEdit(); }}><IconEdit /></Row></>}
        <Divider />
        <Row label={t.messaging.copy} onPress={e => { e.stopPropagation(); onCopy(); }}><IconCopy /></Row>
        {onConvertToTask && <><Divider /><Row label="Convert to Task" onPress={e => { e.stopPropagation(); onConvertToTask(); }}><IconTask /></Row></>}
        {isOwnMessage && <><Divider /><Row label={t.messaging.delete} onPress={e => { e.stopPropagation(); onDelete(); }} destructive><IconDelete /></Row></>}
      </div>
    </div>
  );
}

// ── Portal sub-components ────────────────────────────────────────────────────

function PortalPill({ onReaction, isOwnMessage }: { onReaction: (e: string) => void; isOwnMessage: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.animate(
      [
        { opacity: 0, transform: 'scale(0.7)', transformOrigin: isOwnMessage ? 'right center' : 'left center' },
        { opacity: 1, transform: 'scale(1.04)', transformOrigin: isOwnMessage ? 'right center' : 'left center', offset: 0.6 },
        { opacity: 1, transform: 'scale(1)', transformOrigin: isOwnMessage ? 'right center' : 'left center' },
      ],
      { duration: 300, easing: 'cubic-bezier(0.34,1.56,0.64,1)', fill: 'forwards' }
    );
  }, [isOwnMessage]);

  return (
    <div ref={ref} style={{ ...GLASS, borderRadius: 999, padding: '6px 10px', display: 'flex', gap: 2 }}>
      {QUICK_REACTIONS.map(emoji => (
        <button
          key={emoji}
          onClick={e => { e.stopPropagation(); onReaction(emoji); }}
          style={{
            width: 38, height: 38, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 22, background: 'transparent',
            transition: 'transform 120ms cubic-bezier(0.34,1.56,0.64,1)',
          }}
          onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.25)')}
          onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
        >{emoji}</button>
      ))}
    </div>
  );
}

function PortalMenu({ messageId, isOwnMessage, onReply, onEdit, onDelete, onCopy, onConvertToTask, t }: {
  messageId: string;
  isOwnMessage: boolean;
  onReply: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onCopy: () => void;
  onConvertToTask?: () => void;
  t: ReturnType<typeof useAppTranslations>['t'];
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.animate(
      [
        { opacity: 0, transform: 'scale(0.82)', transformOrigin: isOwnMessage ? 'top right' : 'top left' },
        { opacity: 1, transform: 'scale(1.03)', transformOrigin: isOwnMessage ? 'top right' : 'top left', offset: 0.65 },
        { opacity: 1, transform: 'scale(1.0)', transformOrigin: isOwnMessage ? 'top right' : 'top left' },
      ],
      { duration: 280, easing: 'cubic-bezier(0.34,1.56,0.64,1)', fill: 'forwards' }
    );
  }, [isOwnMessage]);

  return (
    <div ref={ref} data-message-id={messageId} style={{ ...GLASS, borderRadius: 20, overflow: 'hidden' }}>
      <Row label={t.messaging.reply} onPress={e => { e.stopPropagation(); onReply(); }}><IconReply /></Row>
      {isOwnMessage && <><Divider /><Row label={t.messaging.edit} onPress={e => { e.stopPropagation(); onEdit(); }}><IconEdit /></Row></>}
      <Divider />
      <Row label={t.messaging.copy} onPress={e => { e.stopPropagation(); onCopy(); }}><IconCopy /></Row>
      {onConvertToTask && <><Divider /><Row label="Convert to Task" onPress={e => { e.stopPropagation(); onConvertToTask(); }}><IconTask /></Row></>}
      {isOwnMessage && <><Divider /><Row label={t.messaging.delete} onPress={e => { e.stopPropagation(); onDelete(); }} destructive><IconDelete /></Row></>}
    </div>
  );
}

// ── Shared sub-components ────────────────────────────────────────────────────

function Divider() { return <div style={SEPARATOR} />; }

function Row({ label, onPress, destructive = false, children }: {
  label: string;
  onPress: (e: React.MouseEvent) => void;
  destructive?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onPress}
      className="w-full flex items-center justify-between px-4 transition-colors duration-75"
      style={{ paddingTop: 13, paddingBottom: 13, background: 'transparent' }}
      onMouseEnter={e => (e.currentTarget.style.background = destructive ? 'rgba(255,59,48,0.08)' : 'rgba(0,0,0,0.05)')}
      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
    >
      <span style={{ fontSize: 15, color: destructive ? '#FF3B30' : '#0A0A0A', letterSpacing: '-0.1px' }}>{label}</span>
      <span style={{ color: destructive ? '#FF3B30' : 'rgba(60,60,67,0.55)', display: 'flex' }}>{children}</span>
    </button>
  );
}

function IconReply() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 17 4 12 9 7" /><path d="M20 18v-2a4 4 0 0 0-4-4H4" /></svg>;
}
function IconEdit() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>;
}
function IconCopy() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>;
}
function IconTask() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>;
}
function IconDelete() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>;
}
